import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Building, Plus, Trash, EnvelopeSimple, ArrowsClockwise, Crown, X, ShieldWarning, CopySimple, CheckCircle, ListChecks, DownloadSimple, QrCode, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { superApi, auditApi } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import QRCode from "qrcode";

// Escape HTML so user-controlled fields (invite email, role, etc.) can't break
// out of attributes or inject script tags into the printable hand-off card.
// Pydantic's EmailStr validator catches most malformed inputs, but escaping
// at the boundary is the right defense-in-depth here.
function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function printBulkQrPoster(pending) {
  if (!pending.length) return;
  // Render each pending invite's QR to a data URL, then assemble a printable
  // multi-card poster — 4 cards per page, page-break-after rules handle the
  // overflow when the fleet has more than 4 drivers.
  const cards = await Promise.all(pending.map(async (i) => {
    const dataUri = await QRCode.toDataURL(i.accept_url, { width: 360, margin: 1, color: { dark: "#000", light: "#fff" } });
    const expires = new Date(i.expires_at).toLocaleDateString();
    return `
      <div class="card">
        <div class="brand"><div class="logo">L</div><h1>Load Logic</h1></div>
        <div class="sub">Driver Onboarding · Scan to Join</div>
        <img class="qr" src="${esc(dataUri)}" alt="Invite QR" />
        <div class="meta"><b>${esc(i.email)}</b> &middot; ${esc(i.role.toUpperCase())} &middot; expires ${esc(expires)}</div>
      </div>`;
  }));
  const w = window.open("", "_blank", "width=820,height=1024");
  if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
  w.document.write(`<!doctype html><html><head><title>Load Logic — Onboarding Poster</title>
    <style>
      @page { size: letter; margin: 0.35in; }
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; color:#111; margin:0; padding:0; }
      .grid { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .card { border:2px solid #ff8c00; padding:18px; page-break-inside: avoid; text-align:center; }
      .brand { display:flex; align-items:center; gap:8px; margin-bottom:4px; justify-content:center; }
      .logo { width:28px; height:28px; background:#ff8c00; color:#000; font-weight:900; display:flex; align-items:center; justify-content:center; font-size:16px; }
      h1 { font-size:18px; font-weight:900; margin:0; letter-spacing:-0.02em; }
      .sub { font-size:9px; text-transform:uppercase; letter-spacing:.18em; color:#666; margin-bottom:10px; }
      .qr { width:180px; height:180px; }
      .meta { font-family: monospace; font-size:9px; color:#444; margin-top:8px; word-break: break-all; }
      .card:nth-child(4n) { page-break-after: always; }
    </style></head><body>
    <div class="grid">${cards.join("")}</div>
    <script>setTimeout(() => window.print(), 500);</script>
  </body></html>`);
  w.document.close();
}

const ROLES = ["admin", "dispatcher", "driver"];

export default function SuperPage() {
  const { user } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lastInvite, setLastInvite] = useState(null);

  const allowed = !user || user.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, is] = await Promise.all([superApi.listCompanies(), superApi.listInvites()]);
      setCompanies(cs);
      setInvites(is);
    } catch (e) {
      toast.error("Failed to load super-admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  if (user && user.role !== "super_admin") return <Navigate to="/live" replace />;

  const handleDelete = async (company, force) => {
    try {
      await superApi.deleteCompany(company.id, force);
      toast.success(`Deleted ${company.name}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "company_not_empty") {
        setConfirmDelete({ ...company, blocked: d });
      } else {
        toast.error(typeof d === "string" ? d : "Delete failed");
      }
    }
  };

  return (
    <div className="h-[calc(100vh-72px)] overflow-auto" data-testid="super-page">
      <div className="max-w-7xl mx-auto p-4 lg:p-8 space-y-8">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] flex items-center gap-2">
              <Crown size={11} className="text-[#ff8c00]" weight="fill" /> Super-Admin Console
            </div>
            <h1 className="font-heading font-black text-2xl tracking-tight">Companies & Invites</h1>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2"
            data-testid="new-company-btn"
          >
            <Plus size={12} weight="bold" /> New Company
          </button>
        </div>

        {/* Companies grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="companies-grid">
          {loading && <div className="col-span-full text-xs text-[#71717a] uppercase tracking-[0.2em] py-12 text-center flex items-center justify-center gap-2"><ArrowsClockwise size={12} className="animate-spin" /> Loading companies…</div>}
          {!loading && companies.length === 0 && <div className="col-span-full text-xs text-[#71717a] uppercase tracking-[0.2em] py-12 text-center">No companies provisioned yet</div>}
          {companies.map((c) => (
            <div key={c.id} className="bg-[#0d0d0d] border border-[#2a2a2a] p-4 hover:border-[#ff8c00] transition-colors" data-testid={`company-card-${c.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono">{c.slug}</div>
                  <div className="font-heading font-black text-lg tracking-tight">{c.name}</div>
                </div>
                <Building size={20} className="text-[#4682b4]" />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <Stat label="Users" value={c.user_count} />
                <Stat label="Assets" value={c.asset_count} />
                <Stat label="Jobs" value={c.job_count} />
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => setShowInvite(c)}
                  className="flex-1 py-1.5 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-1"
                  data-testid={`invite-btn-${c.slug}`}
                >
                  <EnvelopeSimple size={11} weight="bold" /> Invite
                </button>
                <button
                  onClick={() => setConfirmDelete(c)}
                  className="px-2 py-1.5 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center"
                  data-testid={`delete-btn-${c.slug}`}
                  title="Delete company"
                >
                  <Trash size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Audit log */}
        <AuditLogSection />

        {/* Invites list */}
        <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
          <div className="border-b border-[#2a2a2a] px-4 py-3 flex items-center justify-between">
            <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Pending Invites</h2>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{invites.filter((i) => !i.accepted_at).length} pending · {invites.length} total</span>
              {invites.filter((i) => !i.accepted_at && i.accept_url).length > 0 && (
                <button
                  onClick={() => printBulkQrPoster(invites.filter((i) => !i.accepted_at && i.accept_url))}
                  className="px-3 py-1.5 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1"
                  data-testid="print-all-qrs"
                  title="Print one poster with every pending invite QR"
                >
                  <Printer size={10} weight="bold" /> Print Poster
                </button>
              )}
            </div>
          </div>
          <div>
            {invites.length === 0 && <div className="px-4 py-6 text-xs text-[#71717a] uppercase tracking-[0.2em] text-center">No invites yet</div>}
            {invites.map((i) => (
              <div key={i.id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[#2a2a2a] last:border-b-0 items-center text-xs" data-testid={`invite-row-${i.id}`}>
                <div className="col-span-4 font-mono truncate">{i.email}</div>
                <div className="col-span-3 truncate text-[#a1a1aa]">{i.company_name}</div>
                <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-[#4682b4]">{i.role}</div>
                <div className="col-span-2 text-[10px] uppercase tracking-[0.2em]">
                  {i.accepted_at ? (
                    <span className="text-[#22c55e] flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Accepted</span>
                  ) : new Date(i.expires_at) < new Date() ? (
                    <span className="text-[#ef4444]">Expired</span>
                  ) : (
                    <span className="text-[#ff8c00]">Pending</span>
                  )}
                </div>
                <div className="col-span-1 text-right flex items-center justify-end gap-1">
                  {!i.accepted_at && i.accept_url && (
                    <button
                      onClick={() => setLastInvite(i)}
                      className="w-7 h-7 border border-[#2a2a2a] hover:border-[#22c55e] hover:text-[#22c55e] inline-flex items-center justify-center"
                      data-testid={`show-qr-${i.id}`}
                      title="Show QR / re-print hand-off card"
                    >
                      <QrCode size={10} weight="bold" />
                    </button>
                  )}
                  {!i.accepted_at && (
                    <button
                      onClick={async () => { await superApi.revokeInvite(i.id); toast.success("Invite revoked"); load(); }}
                      className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] inline-flex items-center justify-center"
                      data-testid={`revoke-${i.id}`}
                      title="Revoke invite"
                    >
                      <Trash size={10} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCreate && <CreateCompanyDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showInvite && <InviteDialog company={showInvite} onClose={() => setShowInvite(null)} onCreated={(inv) => { setShowInvite(null); setLastInvite(inv); load(); }} />}
      {confirmDelete && <ConfirmDeleteDialog company={confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={(force) => handleDelete(confirmDelete, force)} />}
      {lastInvite && <InviteCreatedDialog invite={lastInvite} onClose={() => setLastInvite(null)} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#1a1a1a] py-2 px-1">
      <div className="font-mono font-bold text-lg">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">{label}</div>
    </div>
  );
}

function Modal({ title, onClose, children, testId, color = "#ff8c00" }) {
  return (
    <div className="fixed inset-0 z-[3000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0d0d0d] border w-full max-w-md" style={{ borderColor: color }}>
        <div className="px-5 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <div className="font-heading font-black tracking-tight">{title}</div>
          <button onClick={onClose} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ef4444] flex items-center justify-center"><X size={12} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function CreateCompanyDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [seedDemo, setSeedDemo] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await superApi.createCompany({ name: name.trim(), slug: slug.trim() || undefined, seed_demo_data: seedDemo });
      toast.success(`Created ${name}`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New Company" onClose={onClose} testId="create-company-dialog">
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Company name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Logistics" className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm mb-3" data-testid="company-name-input" />
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Slug (optional)</label>
      <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme-logistics" className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm mb-3 font-mono" data-testid="company-slug-input" />
      <label className="flex items-center gap-2 text-xs cursor-pointer mb-4">
        <input type="checkbox" checked={seedDemo} onChange={(e) => setSeedDemo(e.target.checked)} data-testid="seed-demo-checkbox" />
        <span>Seed with demo data (12 assets, 5 jobs, 3 zones, 3 axle templates)</span>
      </label>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em] font-bold">Cancel</button>
        <button onClick={submit} disabled={busy} className="px-4 py-1.5 bg-[#ff8c00] text-black text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="create-confirm">
          {busy && <ArrowsClockwise size={10} className="animate-spin" />} Create
        </button>
      </div>
    </Modal>
  );
}

function InviteDialog({ company, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("dispatcher");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim()) return toast.error("Email required");
    setBusy(true);
    try {
      const inv = await superApi.createInvite({ email: email.trim(), company_id: company.id, role });
      toast.success("Invite email sent");
      onCreated(inv);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Invite to ${company.name}`} onClose={onClose} testId="invite-dialog" color="#4682b4">
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Email</label>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="newuser@example.com" className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#4682b4] outline-none text-sm mb-3" data-testid="invite-email-input" />
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Role</label>
      <div className="flex border border-[#2a2a2a] mb-4">
        {ROLES.map((r) => (
          <button key={r} onClick={() => setRole(r)} className={`flex-1 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${role === r ? "bg-[#4682b4] text-white" : "text-[#a1a1aa]"}`} data-testid={`invite-role-${r}`}>
            {r}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em] font-bold">Cancel</button>
        <button onClick={submit} disabled={busy} className="px-4 py-1.5 bg-[#4682b4] text-white text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="invite-confirm">
          {busy && <ArrowsClockwise size={10} className="animate-spin" />} Send Invite
        </button>
      </div>
    </Modal>
  );
}

function ConfirmDeleteDialog({ company, onCancel, onConfirm }) {
  const blocked = company.blocked;
  return (
    <Modal title="Delete Company" onClose={onCancel} testId="delete-company-dialog" color="#ef4444">
      <div className="flex items-start gap-2 mb-3">
        <ShieldWarning size={28} weight="fill" className="text-[#ef4444] shrink-0" />
        <div>
          <div className="text-sm font-semibold">{company.name}</div>
          <div className="text-[11px] text-[#71717a] font-mono">{company.slug}</div>
        </div>
      </div>
      {blocked ? (
        <>
          <div className="bg-[#ef4444]/10 border border-[#ef4444]/40 p-3 text-[11px] text-[#ef4444] mb-3 leading-relaxed">
            {blocked.message}
          </div>
          <div className="text-[11px] text-[#a1a1aa] mb-4 leading-relaxed">
            <strong className="text-[#ef4444]">Force-delete will cascade-wipe</strong> all {blocked.user_count} users, {blocked.asset_count} assets, all jobs, zones, alerts, photos, trails, push subscriptions, axle templates, and invites belonging to this company. This cannot be undone.
          </div>
        </>
      ) : (
        <div className="text-[11px] text-[#a1a1aa] mb-4">Delete this company? It has no users or assets attached.</div>
      )}
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em] font-bold">Cancel</button>
        <button onClick={() => onConfirm(true)} className="px-4 py-1.5 bg-[#ef4444] text-white text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="delete-confirm">
          {blocked ? "Force Delete" : "Delete"}
        </button>
      </div>
    </Modal>
  );
}

function InviteCreatedDialog({ invite, onClose }) {
  const [tab, setTab] = useState("link"); // "link" | "qr"
  const copy = () => {
    navigator.clipboard.writeText(invite.accept_url);
    toast.success("Link copied");
  };

  const downloadQrPng = () => {
    // Render the SVG to a PNG via offscreen canvas, then trigger a download.
    const svg = document.querySelector('[data-testid="invite-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img64 = "data:image/svg+xml;base64," + window.btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = 1024;
      c.height = 1024;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 64, 64, 896, 896);
      const a = document.createElement("a");
      a.download = `load-logic-invite-${invite.email.replace(/[^a-z0-9]/gi, "_")}.png`;
      a.href = c.toDataURL("image/png");
      a.click();
      toast.success("QR PNG downloaded");
    };
    img.src = img64;
  };

  const printCard = () => {
    // Open a tiny print-friendly hand-off card with the QR + instructions.
    const svg = document.querySelector('[data-testid="invite-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const dataUri = "data:image/svg+xml;base64," + window.btoa(unescape(encodeURIComponent(xml)));
    const expires = new Date(invite.expires_at).toLocaleString();
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>Load Logic — Driver Onboarding</title>
      <style>
        @page { size: letter; margin: 0.5in; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; color:#111; margin:0; padding:24px; }
        .card { border:2px solid #ff8c00; padding:28px; max-width: 640px; margin: 0 auto; }
        .brand { display:flex; align-items:center; gap:10px; margin-bottom: 8px; }
        .logo { width:40px; height:40px; background:#ff8c00; color:#000; font-weight:900; display:flex; align-items:center; justify-content:center; font-size:20px; }
        h1 { font-size:24px; font-weight:900; margin:0; letter-spacing:-0.02em; }
        .sub { font-size:11px; text-transform:uppercase; letter-spacing:.18em; color:#666; margin-bottom:24px; }
        .qrwrap { text-align:center; margin: 12px 0 18px; }
        .qrwrap img { width: 320px; height: 320px; }
        .row { display:flex; gap:8px; align-items:center; font-size:12px; margin: 6px 0; }
        .bullet { width:6px; height:6px; background:#ff8c00; border-radius:50%; flex:none; }
        .footer { margin-top: 20px; font-size: 10px; color:#666; border-top:1px solid #eee; padding-top:12px; }
        .meta { font-family: monospace; font-size:10px; color:#888; word-break: break-all; }
        ol { padding-left: 20px; font-size: 13px; line-height: 1.6; }
        b { color:#000; }
      </style></head><body>
      <div class="card">
        <div class="brand"><div class="logo">L</div><h1>Load Logic</h1></div>
        <div class="sub">Driver Onboarding · Scan to Join</div>
        <div class="qrwrap"><img src="${esc(dataUri)}" alt="Invite QR" /></div>
        <ol>
          <li>Open your phone camera and <b>scan the QR code above</b></li>
          <li>Tap the link that appears</li>
          <li>Set a password — that's it. You're in.</li>
          <li>After login, tap <b>"Install App"</b> and <b>"Start GPS broadcast"</b></li>
        </ol>
        <div class="footer">
          <div class="row"><span class="bullet"></span><span>Invited: <b>${esc(invite.email)}</b> · Role: <b>${esc(invite.role)}</b></span></div>
          <div class="row"><span class="bullet"></span><span>Expires: ${esc(expires)}</span></div>
          <div class="row" style="margin-top:8px"><span class="meta">${esc(invite.accept_url)}</span></div>
        </div>
      </div>
      <script>setTimeout(() => window.print(), 350);</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Modal title="Invite Created" onClose={onClose} testId="invite-created-dialog" color="#22c55e">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle size={20} weight="fill" className="text-[#22c55e]" />
        <div>
          <div className="text-sm font-bold">Email sent to {invite.email}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">expires {new Date(invite.expires_at).toLocaleString()}</div>
        </div>
      </div>

      {/* Tabs: link vs QR */}
      <div className="flex border-b border-[#2a2a2a] mb-3">
        <button
          onClick={() => setTab("link")}
          className={`flex-1 py-2 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2 transition-colors ${tab === "link" ? "text-[#22c55e] border-b-2 border-[#22c55e]" : "text-[#71717a] hover:text-white"}`}
          data-testid="invite-tab-link"
        >
          <CopySimple size={11} weight="bold" /> Magic Link
        </button>
        <button
          onClick={() => setTab("qr")}
          className={`flex-1 py-2 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2 transition-colors ${tab === "qr" ? "text-[#22c55e] border-b-2 border-[#22c55e]" : "text-[#71717a] hover:text-white"}`}
          data-testid="invite-tab-qr"
        >
          <QrCode size={11} weight="bold" /> QR Code
        </button>
      </div>

      {tab === "link" && (
        <div data-testid="invite-link-pane">
          <div className="text-[11px] text-[#a1a1aa] mb-2">Share the magic link directly:</div>
          <div className="flex items-center gap-2 mb-3">
            <input readOnly value={invite.accept_url} className="flex-1 px-2 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-[11px] font-mono text-[#4682b4]" data-testid="invite-link-input" />
            <button onClick={copy} className="px-3 py-2 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="copy-invite-link">
              <CopySimple size={11} /> Copy
            </button>
          </div>
        </div>
      )}

      {tab === "qr" && (
        <div data-testid="invite-qr-pane">
          <div className="text-[11px] text-[#a1a1aa] mb-3 leading-relaxed">
            Have the driver scan with their phone camera — they go straight to set-password, then land on the driver app.
          </div>
          <div className="bg-white p-4 flex items-center justify-center mb-3">
            <QRCodeSVG
              value={invite.accept_url}
              size={220}
              level="M"
              includeMargin={false}
              data-testid="invite-qr-svg"
              imageSettings={{
                src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' fill='%23ff8c00'/><text x='12' y='17' font-family='sans-serif' font-weight='900' font-size='14' text-anchor='middle' fill='black'>L</text></svg>",
                height: 36,
                width: 36,
                excavate: true,
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={printCard}
              className="px-3 py-2 border border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00] hover:text-black text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2"
              data-testid="invite-qr-print"
            >
              <Printer size={11} weight="bold" /> Print Hand-off Card
            </button>
            <button
              onClick={downloadQrPng}
              className="px-3 py-2 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2"
              data-testid="invite-qr-download"
            >
              <DownloadSimple size={11} weight="bold" /> Download PNG
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end mt-4">
        <button onClick={onClose} className="px-4 py-1.5 bg-[#22c55e] text-black text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="invite-created-done">Done</button>
      </div>
    </Modal>
  );
}

function AuditLogSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState(null); // null = all

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (actionFilter) params.action = actionFilter;
      const r = await auditApi.list(params);
      setRows(r);
    } catch (_e) {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [actionFilter]); // eslint-disable-line

  const filtered = rows.filter((r) =>
    !filter ||
    r.action?.toLowerCase().includes(filter.toLowerCase()) ||
    r.actor_email?.toLowerCase().includes(filter.toLowerCase()) ||
    (r.target_name || "").toLowerCase().includes(filter.toLowerCase())
  );

  const actionColor = (action) => {
    if (!action) return "#71717a";
    if (action.includes("delete")) return "#ef4444";
    if (action.includes("create") || action.includes("invite.create")) return "#22c55e";
    if (action.includes("view_company")) return "#4682b4";
    return "#ff8c00";
  };

  const ACTION_PILLS = [
    { label: "All", value: null },
    { label: "Companies", value: "company.create" },
    { label: "Company Delete", value: "company.delete" },
    { label: "Invites Sent", value: "invite.create" },
    { label: "Invites Revoked", value: "invite.revoke" },
    { label: "View-As", value: "super.view_company" },
  ];

  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="audit-log-section">
      <div className="border-b border-[#2a2a2a] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-bold text-sm tracking-tight uppercase flex items-center gap-2">
          <ListChecks size={14} className="text-[#ff8c00]" weight="bold" /> Audit Log
        </h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by actor / action / target"
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1 text-[11px] font-mono w-60"
            data-testid="audit-filter-input"
          />
          <a
            href={auditApi.exportCsvUrl(actionFilter ? { action: actionFilter } : {})}
            className="border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1"
            data-testid="audit-csv-export"
            title="Download CSV"
          >
            <DownloadSimple size={11} weight="bold" /> CSV
          </a>
          <button
            onClick={load}
            className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ff8c00] hover:text-[#ff8c00] inline-flex items-center justify-center"
            title="Refresh"
            data-testid="audit-refresh"
          >
            <ArrowsClockwise size={11} />
          </button>
        </div>
      </div>
      <div className="px-4 py-2 border-b border-[#2a2a2a] flex items-center gap-1.5 flex-wrap" data-testid="audit-action-pills">
        {ACTION_PILLS.map((p) => {
          const active = actionFilter === p.value;
          return (
            <button
              key={p.label}
              onClick={() => setActionFilter(p.value)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${active ? "bg-[#ff8c00] text-black border-[#ff8c00]" : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00] hover:text-[#ff8c00]"}`}
              data-testid={`audit-pill-${p.value || "all"}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {loading && <div className="px-4 py-6 text-xs text-[#71717a] uppercase tracking-[0.2em] text-center">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="px-4 py-6 text-xs text-[#71717a] uppercase tracking-[0.2em] text-center">No audit events match the filters</div>}
        {!loading && filtered.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[#2a2a2a] last:border-b-0 items-center text-xs" data-testid={`audit-row-${r.id}`}>
            <div className="col-span-3 font-mono text-[10px] text-[#a1a1aa]">{new Date(r.ts).toLocaleString()}</div>
            <div className="col-span-3 font-mono truncate" title={r.actor_email}>{r.actor_email}</div>
            <div className="col-span-2">
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border" style={{ color: actionColor(r.action), borderColor: actionColor(r.action) + "60" }}>
                {r.action}
              </span>
            </div>
            <div className="col-span-4 truncate text-[#a1a1aa]" title={r.target_name || ""}>
              {r.target_name || (r.details && Object.keys(r.details).length ? JSON.stringify(r.details) : "—")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

